import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import PaymentPopup from '../../components/PaymentPopup/PaymentPopup';
import authService from '../../services/auth.service';
import billService, { Bill } from '../../services/bill.service';
import './History.css';

// Component for countdown timer
const CountdownTimer: React.FC<{ expiredAt: string }> = ({ expiredAt }) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  useEffect(() => {
    const updateTimeRemaining = () => {
      const now = Date.now();
      const expired = new Date(expiredAt).getTime();
      const remaining = Math.max(0, expired - now);
      setTimeRemaining(remaining);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [expiredAt]);

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (timeRemaining <= 0) {
    return <span className="countdown-expired">Đã hết hạn</span>;
  }

  return (
    <span className="countdown-timer">
      Còn lại: <strong>{formatTime(timeRemaining)}</strong>
    </span>
  );
};

interface HistoryProps {
  onClose?: () => void; // Callback when used in popup
}

const History: React.FC<HistoryProps> = ({ onClose }) => {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState<string>('');
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadHistory = async () => {
      // Check if user is logged in
      const token = authService.getAccessToken();
      if (!token) {
        setError('Vui lòng đăng nhập để xem lịch sử hóa đơn');
        setLoading(false);
        return;
      }

      try {
        // Validate token to get username
        const validateResponse = await authService.validateToken();
        if (!validateResponse.success || !validateResponse.username) {
          setError('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại');
          setLoading(false);
          return;
        }

        const user = validateResponse.username;
        setUsername(user);

        // Fetch bill history
        const response = await billService.getHistoryBills(user);
        if (response.success && response.bills) {
          setBills(response.bills);
        } else {
          setError(response.message || 'Không thể tải lịch sử hóa đơn');
        }
      } catch (err) {
        console.error('Error loading history:', err);
        setError('Có lỗi xảy ra khi tải lịch sử hóa đơn');
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, []);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'Đã thanh toán';
      case 'pending':
        return 'Chờ thanh toán';
      case 'expired':
        return 'Đã hết hạn';
      case 'failed':
        return 'Thất bại';
      default:
        return status;
    }
  };

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'pending':
        return 'status-pending';
      case 'expired':
        return 'status-expired';
      case 'failed':
        return 'status-failed';
      default:
        return '';
    }
  };

  const handlePaymentClick = async (bill: Bill) => {
    // Fetch bill details with QR code
    const response = await billService.getBillById(bill.id);
    if (response.success && response.bill) {
      setSelectedBill(response.bill);
      setShowPaymentPopup(true);
    } else {
      alert(response.message || 'Không thể lấy thông tin thanh toán');
    }
  };

  const handlePaymentSuccess = () => {
    // Reload history after successful payment
    const loadHistory = async () => {
      try {
        const response = await billService.getHistoryBills(username);
        if (response.success && response.bills) {
          setBills(response.bills);
        }
      } catch (err) {
        console.error('Error reloading history:', err);
      }
    };
    loadHistory();
  };

  if (loading) {
    return (
      <div className="history-page">
        <div className="history-container">
          <div className="loading">Đang tải...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-page">
        <div className="history-container">
          <h1>Lịch sử hóa đơn</h1>
          <div className="error-message">{error}</div>
          <button className="back-button" onClick={() => onClose ? onClose() : navigate('/')}>
            {onClose ? 'Đóng' : 'Về trang chủ'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {!onClose && (
        <Helmet>
          <title>Lịch sử đơn hàng</title>

          <meta name="description" content="Lịch sử đơn hàng" />

          {/* og */}
          <meta property="og:title" content="Lịch sử đơn hàng" />
          <meta
            property="og:description"
            content="Lịch sử đơn hàng"
          />
          <meta property="og:image" content="/logo512.jpg" />
        </Helmet>
      )}
      <div className="history-page">
        <div className="history-container">
          <h1>Lịch sử hóa đơn</h1>
          {username && (
            <p className="username-info">
              Người dùng: <strong>{username}</strong>
            </p>
          )}

          {bills.length === 0 ? (
            <div className="empty-state">
              <p>Bạn chưa có hóa đơn nào.</p>
              {onClose && (
                <button className="back-button" onClick={onClose}>
                  Đóng
                </button>
              )}
              {!onClose && (
                <button className="back-button" onClick={() => navigate('/')}>
                  Về trang chủ
                </button>
              )}
            </div>
          ) : (
            <div className="bills-list">
              {bills.map((bill) => (
                <div key={bill.id} className="bill-card">
                  <div className="bill-header">
                    <div className="bill-id">Mã hóa đơn: {bill.id}</div>
                    <span
                      className={`status-badge ${getStatusClass(bill.status)}`}
                    >
                      {getStatusLabel(bill.status)}
                    </span>
                  </div>
                  <div className="bill-body">
                    <div className="bill-info-row">
                      <span className="label">Số tiền:</span>
                      <span className="value amount">
                        {formatCurrency(bill.billAmount)}
                      </span>
                    </div>
                    <div className="bill-info-row">
                      <span className="label">Số ô đã mua:</span>
                      <span className="value">{bill.cells.length} ô</span>
                    </div>
                    <div className="bill-info-row">
                      <span className="label">Danh sách ô:</span>
                      <span className="value cells-list">
                        {bill.cells.slice(0, 10).join(', ')}
                        {bill.cells.length > 10 &&
                          ` ... (+${bill.cells.length - 10} ô khác)`}
                      </span>
                    </div>
                    <div className="bill-info-row">
                      <span className="label">Ngày tạo:</span>
                      <span className="value">
                        {formatDate(bill.createdAt)}
                      </span>
                    </div>
                    {bill.expiredAt && (
                      <div className="bill-info-row">
                        <span className="label">Hết hạn:</span>
                        <span className="value">
                          {formatDate(bill.expiredAt)}
                        </span>
                      </div>
                    )}
                    {bill.status === 'pending' && bill.expiredAt && (
                      <div className="bill-info-row countdown-row">
                        <span className="label">Thời gian còn lại:</span>
                        <span className="value">
                          <CountdownTimer expiredAt={bill.expiredAt} />
                        </span>
                      </div>
                    )}
                    {bill.status === 'pending' && (
                      <div className="bill-actions">
                        <button
                          className="payment-button"
                          onClick={() => handlePaymentClick(bill)}
                        >
                          Thanh toán
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment Popup */}
        {showPaymentPopup && selectedBill && selectedBill.expAt && (
          <PaymentPopup
            isOpen={showPaymentPopup}
            billId={selectedBill.id}
            expAt={selectedBill.expAt}
            qr={selectedBill.qr}
            onClose={() => {
              setShowPaymentPopup(false);
              setSelectedBill(null);
            }}
            onPaymentSuccess={handlePaymentSuccess}
          />
        )}
      </div>
    </>
  );
};

export default History;
